import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import { useWebtoonStoriesQuery } from '@hooks/query/useWebtoonStoriesQuery';
import type { WebtoonStoryItem } from '@hooks/query/useWebtoonStoriesQuery';
import { Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowRight, Icon } from 'chatie-design-system/icon';

import { OptimizeEFinderImage } from '@components/common/EFinderImage';
import SwipeableListContainer from '@components/common/SwipableListContainer';
import SafeArea from '@components/layout/SafeArea';

import { media } from '@styles/css';
import { getWebtoonStoryPath } from '@utils/webtoon';
import { useWebView } from '@utils/webview';

const sectionCSS = css`
  padding-top: ${Spacing.medium}px;
`;

const headerCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${Spacing.medium}px ${Spacing.small}px;

  ${media.tablet} {
    padding: 0 ${Spacing.xlarge}px ${Spacing.small}px;
  }

  ${media.desktop} {
    padding: 0 ${Spacing.xxlarge}px ${Spacing.small}px;
  }

  .title-container {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .title {
    ${Typography.h5};
  }

  .beta-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    padding: 0 6px;
    border-radius: 4px;
    background: ${theme.var.gray100};
    color: ${theme.var.gray600};
    font-size: 10px;
    font-weight: 900;
    line-height: 1;
  }

  .more-container {
    display: flex;
    min-width: 53px;
    align-items: center;
    flex-direction: row;
    color: ${theme.var.gray400};
    text-decoration: none;
    ${Typography.body14};
  }

  .more {
    color: ${theme.var.gray400};
  }
`;

const listCSS = css`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(1, 1fr);
  gap: 8px;
  padding: 8px ${Spacing.medium}px ${Spacing.small}px;

  ${media.tablet} {
    padding: 8px ${Spacing.xlarge}px ${Spacing.small}px;
  }

  ${media.desktop} {
    padding: 8px ${Spacing.xxlarge}px ${Spacing.small}px;
  }
`;

const cardCSS = (theme: Theme) => css`
  display: inline-block;
  width: 120px;
  cursor: pointer;
  color: inherit;
  text-decoration: none;

  .image {
    position: relative;
    width: 120px;
    height: 90px;
    overflow: hidden;
    flex-shrink: 0;
    border-radius: 4px;
    background: ${theme.var.gray50};
  }

  .placeholder {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
    color: ${theme.var.gray400};
    ${Typography.body11};
  }

  .info-container {
    background: ${theme.var.white};
    border-radius: 0 0 4px 4px;
    padding: ${Spacing.xsmall}px;
    gap: ${Spacing.xxsmall}px;
  }

  .title {
    ${Typography.h6};
    color: ${theme.var.black};
  }

  .nickname {
    ${Typography.body12};
    color: ${theme.var.gray500};
  }
`;

const WebtoonStoryCard = ({
  href,
  story,
  onClick,
}: {
  href: string;
  story: WebtoonStoryItem;
  onClick: () => void;
}) => (
  <Link href={href} passHref>
    <a
      css={cardCSS}
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      <div className="image">
        {story.coverImage ? (
          <OptimizeEFinderImage
            image={story.coverImage}
            height={90}
            width={120}
            h={54}
          />
        ) : (
          <div className="placeholder">채티 웹툰</div>
        )}
      </div>
      <div className="info-container">
        <p className="ellipsis title">{story.title}</p>
        <p className="ellipsis nickname">
          {story.authorName || story.subtitle}
        </p>
      </div>
    </a>
  </Link>
);

const WebtoonStoryList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { openPageOrMove } = useWebView();
  const { isMounted, webtoonStories } = useWebtoonStoriesQuery();

  if (!isMounted || !webtoonStories.length) {
    return null;
  }

  return (
    <SafeArea>
      <section css={sectionCSS}>
        <div css={headerCSS}>
          <div className="title-container">
            <p className="title">{t('채티 웹툰')}</p>
            <span className="beta-badge">BETA</span>
          </div>
          <Link href="/webtoon" passHref>
            <a
              className="more-container"
              onClick={(event) => {
                event.preventDefault();
                openPageOrMove('/webtoon');
              }}
            >
              <p className="more">{t('더보기')}</p>
              <Icon
                icon={ArrowRight}
                color={theme.var.gray400}
                size={Sizes.small}
              />
            </a>
          </Link>
        </div>
        <SwipeableListContainer className="galleryCardListContainer">
          <div css={listCSS}>
            {webtoonStories.map((story) => {
              const path = getWebtoonStoryPath(story.webtoonId);

              return (
                <WebtoonStoryCard
                  key={story.webtoonId}
                  href={path}
                  story={story}
                  onClick={() => {
                    openPageOrMove(path);
                  }}
                />
              );
            })}
          </div>
        </SwipeableListContainer>
      </section>
    </SafeArea>
  );
};

export default WebtoonStoryList;
